import { AgentCommissionStatus, AgentCommissionDto } from "app/shared/model/AgentCommissionDto";
import { Country, School } from "app/shared/model";
import { isBefore as isDateBefore, format as formatDate } from 'date-fns';
import DateHelper from "app/helpers/dateHelper";
import {toSchoolNumericId, toSchoolNumericIdFromSchool} from "app/pages/AgentPricing/utils/schoolIds";

export interface RatesTable {
    rules: RatesRule[]
    versionCode: string
    countries: CountryGroup[]
    rates: Rates
    dates: RatesDates
    status: AgentCommissionStatus
}

export interface RatesDates {
    startDate: string
    endDate?: string
    minChangeDate: string
}

export interface RatesRule {
    code: string
    name: string
}

export interface CountryGroup {
    id: string
    name: string
    columns: RatesColumn[]
}

export interface RatesColumn {
    id: string
    name: string
    isSchool: boolean
}

export type Rates = {
    [rowAndColumnId: string]: string
}

export type RateValue = {
    columnId: string, ruleId: string, rate: string 
}

export function getRate(rates: Rates, columnId: string, ruleId: string) {
    return rates[columnId + '|' + ruleId]
}

function setRate(rates: Rates, columnId: string, ruleId: string, rate: string) : Rates {
    return {
        ... rates,
        [columnId + '|' + ruleId]: rate
    }
}

function setRates(rates: Rates, values: RateValue[]) : Rates {
    const valuesMap =  values.reduce((result, val) => 
    ({ 
        ...result, 
        [val.columnId + '|' + val.ruleId]: val.rate 
    }), {});
    
    return {
        ... rates,
        ... valuesMap
    }
}

export function mapToRatesTable(dto: AgentCommissionDto, countries: Country[], schools: School[], editMode: boolean) : RatesTable {
    const rules = dto.rules.map((x) => {
        return { code: x.code, name: x.name }
    })

    let rates : Rates = {}; 
    const countryGroups : CountryGroup[] = [];

    function formatRate(value: string) {
        if (parseFloat(value) === 0)
            return editMode ? '' : '-';
        return value + (editMode ? '' : '%');
    }

    dto.countries.forEach(x => {
        const country = countries.find(y => y.id == x.countryId); 
        const columns : RatesColumn[] = [];
        
        x.schools.forEach(z => {
            const zId = toSchoolNumericId(z.schoolId);
            const school = zId
                ? schools.find(s => toSchoolNumericIdFromSchool(s) === zId)
                : undefined;

            const column: RatesColumn = {
                id: z.schoolId,
                name: school ? school.name : "Unknown school",
                isSchool: true
            };

            const rateValues : RateValue[] = [];
            Object.keys(z.percentageRates).forEach(k => {
                rateValues.push({
                    columnId: z.schoolId,
                    ruleId: k,
                    rate: formatRate(z.percentageRates[k])
                })
            });
            rates = setRates(rates, rateValues);

            columns.push(column);
        })
               
        if (!x.allSchoolsSpecified) {
            columns.push({
                id: x.countryId, 
                name: x.schools.length > 0 ? "Other schools" : "All schools", 
                isSchool: false
            })

            const rateValues : RateValue[] = []; 
            Object.keys(x.percentageRates).forEach(k => {
                rateValues.push({
                    columnId: x.countryId, 
                    ruleId: k, 
                    rate: formatRate(x.percentageRates[k])
                })
            }); 
            rates = setRates(rates, rateValues); 
        }

        const countryGroup : CountryGroup = {
            id: x.countryId,
            name: country !== undefined ? country.name : "Unknown country", 
            columns: columns
        };
        countryGroups.push(countryGroup); 
    });

    let startDate = DateHelper.ToDate(dto.startDate);
    const minChangeDate = DateHelper.ToDate(dto.minChangeDate);
    if (editMode && isDateBefore(startDate, minChangeDate)) 
        startDate = minChangeDate;

    return {
        rules: rules, 
        versionCode: dto.versionCode, 
        countries: countryGroups, 
        rates: rates,
        dates: {
            startDate: formatDate(startDate, 'yyyy-MM-dd'),
            endDate: dto.endDate,
            minChangeDate: dto.minChangeDate
        }, 
        status: dto.status
    } 
}